import { useEffect, useRef, useState } from 'react';
import { api } from '../../shared/api/client';
import type { ClassLiveState } from '../../shared/api/types';
import { Alert, Button, Card } from '../../shared/ui/components';
import type { ClassChannel } from './ClassLive';

type Signal = { kind: 'hello' } | { kind: 'offer' | 'answer'; sdp: string } | { kind: 'ice'; candidate: RTCIceCandidateInit } | { kind: 'bye' };
/** Hasta cuántas pantallas transmite el celular del alumno (cada una es una conexión). */
const MAX_VIEWERS = 12;

let iceCache: Promise<RTCIceServer[]> | null = null;
const iceServers = () => (iceCache ??= api.get<{ iceServers: RTCIceServer[] }>('/organization/ice-servers').then((r) => r.iceServers).catch(() => [{ urls: 'stun:stun.l.google.com:19302' }]));

/** Aviso corto de sonido (al maestro, cuando alguien pide cámara). */
export function beep() {
  try {
    const Ctx = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.frequency.value = 880;
    gain.gain.setValueAtTime(0.15, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.5);
    osc.connect(gain).connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.5);
  } catch {
    // Sin sonido disponible: el aviso visual basta.
  }
}

/**
 * El celular del alumno con la cámara aceptada: abre cámara y micrófono (solo ahora, después de que
 * el maestro aceptó) y la manda directo a cada pantalla que la pide. No se graba ni pasa por KEORA.
 */
function CameraBroadcaster({ channel, onStop }: { channel: ClassChannel; onStop: () => void }) {
  const preview = useRef<HTMLVideoElement>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);

  useEffect(() => {
    let alive = true;
    let s: MediaStream | null = null;
    if (!navigator.mediaDevices?.getUserMedia) {
      setProblem('Este navegador no deja usar la cámara.');
      return;
    }
    navigator.mediaDevices
      .getUserMedia({ video: { width: { ideal: 1280 }, height: { ideal: 720 }, facingMode: 'user' }, audio: true })
      .then((m) => {
        if (!alive) return m.getTracks().forEach((t) => t.stop());
        s = m;
        setStream(m);
        if (preview.current) preview.current.srcObject = m;
      })
      .catch((e: { name?: string }) => setProblem(e.name === 'NotAllowedError' ? 'No diste permiso para la cámara. Dale permiso en el candado junto a la dirección.' : 'No se pudo abrir la cámara.'));
    return () => {
      alive = false;
      s?.getTracks().forEach((t) => t.stop());
    };
  }, []);

  useEffect(() => {
    if (!stream) return;
    const peers = new Map<string, RTCPeerConnection>();
    const close = (id: string) => {
      peers.get(id)?.close();
      peers.delete(id);
    };
    const off = channel.onSignal((from, raw) => {
      const data = raw as Signal;
      void (async () => {
        if (data.kind === 'hello') {
          if (peers.size >= MAX_VIEWERS && !peers.has(from)) return;
          close(from);
          const pc = new RTCPeerConnection({ iceServers: await iceServers() });
          peers.set(from, pc);
          stream.getTracks().forEach((t) => pc.addTrack(t, stream));
          pc.onicecandidate = (e) => e.candidate && channel.send(from, { kind: 'ice', candidate: e.candidate.toJSON() });
          pc.onconnectionstatechange = () => ['failed', 'closed'].includes(pc.connectionState) && close(from);
          const offer = await pc.createOffer();
          await pc.setLocalDescription(offer);
          channel.send(from, { kind: 'offer', sdp: offer.sdp });
        } else if (data.kind === 'answer') {
          await peers.get(from)?.setRemoteDescription({ type: 'answer', sdp: data.sdp });
        } else if (data.kind === 'ice') {
          await peers.get(from)?.addIceCandidate(data.candidate).catch(() => undefined);
        } else if (data.kind === 'bye') {
          close(from);
        }
      })();
    });
    return () => {
      off();
      for (const id of [...peers.keys()]) close(id);
    };
  }, [stream, channel]);

  return (
    <Card title="📷 Tu cámara está en la clase">
      {problem ? <Alert>{problem}</Alert> : <video ref={preview} className="class-camera class-camera--mine" autoPlay playsInline muted aria-label="Tu cámara" />}
      <Button variant="danger" onClick={onStop}>
        Apagar mi cámara
      </Button>
    </Card>
  );
}

/** Ver la cámara del alumno (maestro, compañeros y la pantalla de Multimedia). */
export function CameraViewer({ channel, name, className = 'class-camera', startMuted = true }: { channel: ClassChannel; name: string; className?: string; startMuted?: boolean }) {
  const video = useRef<HTMLVideoElement>(null);
  const [connected, setConnected] = useState(false);
  const [muted, setMuted] = useState(startMuted);
  useEffect(() => {
    let pc: RTCPeerConnection | null = null;
    let camera: string | null = null;
    let alive = true;
    const off = channel.onSignal((from, raw) => {
      const data = raw as Signal;
      void (async () => {
        if (data.kind === 'offer') {
          pc?.close();
          camera = from;
          pc = new RTCPeerConnection({ iceServers: await iceServers() });
          pc.ontrack = (e) => {
            if (video.current && e.streams[0]) {
              video.current.srcObject = e.streams[0];
              // Si el navegador no deja sonar sin un toque, se queda sin sonido (con el botón para activarlo).
              video.current.play?.()?.catch(() => setMuted(true));
            }
            setConnected(true);
          };
          pc.onicecandidate = (e) => e.candidate && channel.send(from, { kind: 'ice', candidate: e.candidate.toJSON() });
          await pc.setRemoteDescription({ type: 'offer', sdp: data.sdp });
          const answer = await pc.createAnswer();
          await pc.setLocalDescription(answer);
          channel.send(from, { kind: 'answer', sdp: answer.sdp });
        } else if (data.kind === 'ice' && from === camera) {
          await pc?.addIceCandidate(data.candidate).catch(() => undefined);
        }
      })();
    });
    // Saluda a la cámara (y otra vez cada 5 s mientras no conecte).
    const hello = () => alive && !pc?.connectionState?.match(/connected/) && channel.send('camera', { kind: 'hello' });
    hello();
    const t = setInterval(hello, 5000);
    return () => {
      alive = false;
      clearInterval(t);
      off();
      if (camera) channel.send(camera, { kind: 'bye' });
      pc?.close();
    };
  }, [channel, channel.joins]);
  return (
    <div className="class-camera__wrap">
      <video ref={video} className={className} autoPlay playsInline muted={muted} aria-label={`Cámara de ${name}`} />
      {!connected ? <p className="class-camera__status">Conectando la cámara de {name}…</p> : null}
      <button type="button" className="class-camera__sound" onClick={() => setMuted((m) => !m)}>
        {muted ? '🔇 Activar sonido' : '🔊 Silenciar'}
      </button>
    </div>
  );
}

/** Todo lo de la cámara del alumno en la clase en vivo, para el maestro y para los alumnos. */
export function CameraArea({
  L,
  base,
  teach,
  meId,
  channel,
  note,
  onNote,
  act,
  onChange,
}: {
  L: ClassLiveState;
  base: string;
  teach: boolean;
  meId: string | null;
  channel: ClassChannel;
  note: string | null;
  onNote: (n: string | null) => void;
  act: (fn: () => Promise<unknown>) => Promise<void>;
  onChange: () => void;
}) {
  const run = (fn: () => Promise<unknown>) => act(() => fn().then(onChange));
  const mine = Boolean(L.camera && meId && L.camera.personId === meId);
  return (
    <>
      {teach && L.cameraRequests && L.cameraRequests.length ? (
        <div className="camera-requests" role="alert">
          {L.cameraRequests.map((p) => (
            <div key={p.personId} className="camera-requests__item">
              <span>
                📷 <strong>{p.name}</strong> pide cámara y micrófono
              </span>
              <Button onClick={() => void run(() => api.put(`${base}/live/camera`, { personId: p.personId, accept: true }))}>Aceptar</Button>
              <Button variant="secondary" onClick={() => void run(() => api.put(`${base}/live/camera`, { personId: p.personId, accept: false }))}>
                Rechazar
              </Button>
            </div>
          ))}
        </div>
      ) : null}
      {mine ? <CameraBroadcaster channel={channel} onStop={() => void run(() => api.put(`${base}/live/camera-request`, { wants: false }))} /> : null}
      {L.camera && !mine ? (
        <Card
          title={`📷 ${L.camera.name}`}
          action={
            teach ? (
              <Button variant="danger" onClick={() => void run(() => api.put(`${base}/live/camera`, { personId: null, accept: false }))}>
                Terminar
              </Button>
            ) : null
          }
        >
          <CameraViewer channel={channel} name={L.camera.name} />
        </Card>
      ) : null}
      {!teach && !mine ? (
        <div className="camera-ask">
          {L.myCameraRequest ? (
            <>
              <span className="small">Esperando a que el maestro acepte tu cámara…</span>
              <Button variant="ghost" onClick={() => void run(() => api.put(`${base}/live/camera-request`, { wants: false }))}>
                Cancelar
              </Button>
            </>
          ) : (
            <Button
              variant="secondary"
              onClick={() => {
                onNote(null);
                void run(() => api.put(`${base}/live/camera-request`, { wants: true }));
              }}
            >
              📷 Pedir cámara y micrófono
            </Button>
          )}
          {note ? <span className="small muted">{note}</span> : null}
        </div>
      ) : null}
    </>
  );
}
